import React, { useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Card, Grid, Tooltip, Button, Typography } from '@material-ui/core';
import { BackButton, Container, Panel } from '../../../../Components';
import { CompanyUnits } from './CompanyUnits/CompanyUnits.List';
import { CompanyUnitEditForm } from './CompanyUnits/CompanyUnits.Edit';
import { EditProcess } from './CompanyUnits/Processes';
import { EditAspect } from './CompanyUnits/Aspects';
import { EditTag } from './CompanyUnits/Tags';
import { CompanyUsers } from './Users/Users.List';
import { UserEditForm } from './Users/Users.Edit';
import { UserInviteForm } from './Users/Users.Create';
import { CustomerLogotypeForm } from './LogotypeForm/CustomerLogotypeForm';
import { AzureADSettings } from './AzureAD';
import { ModuleContext } from '../../../../Context/ModuleContext/ModuleContext';
import { AccessLevel, OrganizationFeature, UserContext } from '../../../../Context/UserContext/UserContext';
import { ArrowUpward, Business, Image, People, Security } from '@material-ui/icons';
import { useStyles } from "./Organization.styles";

export const Organization = (props: { suppressHeader?: boolean }) => {
  const { formatMessage } = useIntl();
  const { setHeader } = useContext(ModuleContext);
  const { hasAccessToFeature } = useContext(UserContext);
  const location = useLocation();
  const canManage = hasAccessToFeature(OrganizationFeature.Manage, AccessLevel.Manage);
  const canManageAAD = hasAccessToFeature(OrganizationFeature.AAD, AccessLevel.Manage);
  const classes = useStyles();
  const canAdminister = hasAccessToFeature(OrganizationFeature.Manage, AccessLevel.Administer);
  const isLawPortalOrganization = location.pathname.startsWith('/law-portal/organization');
  const isLawPortalSettings = location.pathname.startsWith('/law-portal/settings');
  const organizationBasePath = isLawPortalOrganization || isLawPortalSettings ? '/law-portal/organization' : '/organization';
  const backToSettingsPath = isLawPortalOrganization || isLawPortalSettings ? '/law-portal/settings' : '/organization';

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (!props.suppressHeader) {
      setHeader(formatMessage({ id: 'organization.header', defaultMessage: 'Organization' }));
    }
  }, [setHeader, formatMessage, props.suppressHeader]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const mainContent = (
    <Grid container spacing={4} className={classes.cardGrid}>
      <OrganizationCard
        titleKey="organization.company-division"
        descriptionKey="organization.company-division.description"
        to={`${organizationBasePath}/company-division`}
        icon={<Business />}
      />
      {canAdminister && (
        <OrganizationCard
          titleKey="organization.users"
          descriptionKey="organization.users.description"
          to={`${organizationBasePath}/users`}
          icon={<People />}
        />
      )}
      {canAdminister && (
        <OrganizationCard
          titleKey="organization.logotype"
          descriptionKey="organization.logotype.description"
          to={`${organizationBasePath}/logotype`}
          icon={<Image />}
        />
      )}
      {canManageAAD && (
        <OrganizationCard
          titleKey="organization.azure-ad"
          descriptionKey="organization.azure-ad.description"
          to={`${organizationBasePath}/azure-ad`}
          icon={<Security />}
        />
      )}
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </Grid>
  );

  return (
    <Container>
      <Routes>
        <Route path="/" element={mainContent} />
        <Route
          path="company-division"
          element={
            <OrganizationSection backLink={backToSettingsPath} titleKey="organization.company-division">
              <CompanyUnits />
            </OrganizationSection>
          }
        />
        {canAdminister && (
          <Route
            path="users"
            element={
              <OrganizationSection backLink={backToSettingsPath} titleKey="organization.users">
                <CompanyUsers />
              </OrganizationSection>
            }
          />
        )}
        {canAdminister && (
          <Route
            path="logotype"
            element={
              <OrganizationSection backLink={backToSettingsPath} titleKey="organization.logotype">
                <CustomerLogotypeForm />
              </OrganizationSection>
            }
          />
        )}
        {canManageAAD && (
          <Route
            path="azure-ad"
            element={
              <OrganizationSection backLink={backToSettingsPath} titleKey="organization.azure-ad">
                <AzureADSettings />
              </OrganizationSection>
            }
          />
        )}
        {canManage && (
          <Route path="company-units/:id" element={<CompanyUnitEditForm />} />
        )}
        {canManage && (
          <Route path="company-units/:companyUnitId/users/new" element={<UserInviteForm />} />
        )}
        {canManage && (
          <Route path="company-units/:companyUnitId/users/:id" element={<UserEditForm />} />
        )}
        {canAdminister && (
          <Route path="users/new" element={<UserInviteForm />} />
        )}
        {canAdminister && (
          <Route path="users/:id" element={<UserEditForm />} />
        )}
        {canManage && (
          <Route path="company-units/:unitId/processes/:id" element={<EditProcess />} />
        )}
        {canManage && (
          <Route path="company-units/:unitId/aspects/:id" element={<EditAspect />} />
        )}
        {canManage && (
          <Route path="company-units/:unitId/tags/:id" element={<EditTag />} />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Container>
  );
};

const OrganizationSection = (props: {
  backLink: string;
  titleKey: string;
  children: React.ReactNode;
}) => (
  <>
    <BackButton link={props.backLink} />
    <Panel titleKey={props.titleKey}>{props.children}</Panel>
  </>
);

const OrganizationCard = (props: {
  titleKey: string;
  descriptionKey: string;
  to: string;
  icon: React.ReactNode;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  return (
    <Grid item md={6} xs={12}>
      <Link to={props.to} className={classes.cardLink}>
        <Card variant="outlined" className={classes.settingsCard}>
          <div className={classes.settingsCardIcon}>{props.icon}</div>
          <div>
            <Typography variant="h3" component="h2" className={classes.settingsCardTitle}>
              {formatMessage({ id: props.titleKey })}
            </Typography>
            <Typography variant="body1" className={classes.settingsCardDescription}>
              {formatMessage({ id: props.descriptionKey })}
            </Typography>
          </div>
        </Card>
      </Link>
    </Grid>
  );
};
